import React, { useMemo, useRef, useState } from 'react';
import { computeNiceTicks } from './chartUtils';

export interface GroupedBarPoint {
  label: string;
  received: number;
  completed: number;
  pending: number;
}

interface AnalyticsGroupedBarChartProps {
  title: string;
  data: GroupedBarPoint[];
  periodControl?: React.ReactNode;
}

interface TooltipState {
  point: GroupedBarPoint;
  x: number;
}

const COLORS = {
  received: '#5eead4',
  completed: '#2dd4bf',
  pending: '#0d9488',
};

export default function AnalyticsGroupedBarChart({
  title,
  data,
  periodControl,
}: Readonly<AnalyticsGroupedBarChartProps>) {
  const plotRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const maxValue = Math.max(...data.map(d => Math.max(d.received, d.completed, d.pending)), 0);
  const yMax = Math.max(maxValue, 1);
  const yTicks = useMemo(() => computeNiceTicks(yMax, 4), [yMax]);
  const tickMax = yTicks[0] || yMax;

  const showTooltip = (point: GroupedBarPoint, event: React.MouseEvent<HTMLDivElement>) => {
    const plot = plotRef.current;
    if (!plot) {
      return;
    }
    const colRect = event.currentTarget.getBoundingClientRect();
    const plotRect = plot.getBoundingClientRect();
    setTooltip({
      point,
      x: colRect.left - plotRect.left + colRect.width / 2,
    });
  };

  return (
    <section className="analytics-chart-card analytics-chart-card--workload">
      <div className="analytics-chart-card-header">
        <div>
          <h3 className="analytics-chart-card-title">{title}</h3>
          <div className="analytics-chart-legend">
            <span>
              <span
                className="analytics-legend-swatch"
                style={{ background: COLORS.received }}
              />
              <span>Received</span>
            </span>
            <span>
              <span
                className="analytics-legend-swatch"
                style={{ background: COLORS.completed }}
              />
              <span>Completed</span>
            </span>
            <span>
              <span
                className="analytics-legend-swatch"
                style={{ background: COLORS.pending }}
              />
              <span>Pending</span>
            </span>
          </div>
        </div>
        {periodControl ? <div className="analytics-chart-controls">{periodControl}</div> : null}
      </div>
      {data.length === 0 ? (
        <p className="analytics-empty">No data available</p>
      ) : (
        <div className="analytics-chart-body">
          <div
            className="analytics-y-axis"
            aria-hidden
          >
            {yTicks.map(tick => (
              <span
                key={`tick-${tick}`}
                className="analytics-y-tick"
              >
                {tick}
              </span>
            ))}
          </div>
          <div
            className="analytics-plot"
            ref={plotRef}
          >
            {tooltip ? (
              <div
                className="analytics-chart-tooltip-float"
                style={{ left: `${tooltip.x}px` }}
                role="tooltip"
              >
                <strong>{tooltip.point.label}</strong>
                <span>Received: {tooltip.point.received}</span>
                <span>Completed: {tooltip.point.completed}</span>
                <span>Pending: {tooltip.point.pending}</span>
              </div>
            ) : null}
            <div
              className="analytics-grid"
              aria-hidden
            >
              {yTicks.map(tick => (
                <div
                  key={`grid-${tick}`}
                  className="analytics-grid-line"
                />
              ))}
            </div>
            <div className="analytics-grouped-bars">
              {data.map(point => (
                <div
                  key={point.label}
                  className="analytics-bar-group-col"
                  onMouseEnter={event => showTooltip(point, event)}
                  onMouseLeave={() => setTooltip(null)}
                >
                  <div className="analytics-bar-group">
                    {(
                      [
                        { key: 'received', value: point.received, color: COLORS.received },
                        { key: 'completed', value: point.completed, color: COLORS.completed },
                        { key: 'pending', value: point.pending, color: COLORS.pending },
                      ] as const
                    ).map(bar => (
                      <div
                        key={bar.key}
                        className="analytics-bar-track analytics-bar-track--grouped"
                      >
                        <div
                          className="analytics-bar-fill"
                          style={{
                            height: `${Math.max(4, (bar.value / tickMax) * 100)}%`,
                            background: bar.color,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="analytics-bar-label-wrap">
                    <span
                      className="analytics-bar-label"
                      title={point.label}
                    >
                      {point.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
